//
// image-set.js
//
// Responsive CSS background images, while we wait for image-set.
//
//
// Data attributes ---------------------------------------------------------------------------------
//
//   Required:
//     data-image-set - e.g: "fairy-small.jpg 320w, fairy-med.jpg 480w, fairy-large.jpg 800w"
//
//   Optional:
//     data-image-set-lazy         - load the image at a later date
//     data-image-set-active-class - add a custom active class string on load,
//                                   e.g: 't-fade-in--active', 'o-100 scale-10'.
//     data-image-set-wrapper      - wrap a number of data-image-set-lazy elements.
//                                   image-set will bind to this element, and load all
//                                   data-image-set descendants when an image-set/load
//                                   event is dispatched on it.
//
//   Added when loaded:
//     data-image-set-loaded="true"
//
//
// Custom events -----------------------------------------------------------------------------------
//
//   image-set/load    - dispatch this event on a data-image-set-lazy or -wrappr element to initiate
//                       loading
//
//   image-set/loaded  - gets fired when an item is loaded
//
//
//

import load_image from '../helpers/load-image';


// state
// -------------------------------------

function state() {
  if (!window.__image_set_state) {
    window.__image_set_state = {
      timeout: null,
      items: [],
      resize_bound: false,
    };
  }

  return window.__image_set_state;
}


// pixel_ratio
// -------------------------------------

function pixel_ratio() {
  return window.devicePixelRatio ? window.devicePixelRatio : 1;
}


// parse_srcset
// -------------------------------------

function parse_srcset(str) {
  return str.split(', ')
    .map(size => size.split(' '))
    .map(parts => ({ url: parts[0], w: parseInt(parts[1], 10) }))
    .sort((a, b) => a.w - b.w);
}


// get_image_set
// -------------------------------------
// ->
// [
//   { url, w }, ...
// ]

function get_image_set(el) {
  const image_set = el.getAttribute('data-image-set');
  return image_set ? parse_srcset(image_set) : [];
}


// best_size
// -------------------------------------
// - get best item for the given element width

function best_size(sizes, w) {
  const matches = sizes.filter(sz => sz.w > w);
  return matches[0] || sizes[sizes.length - 1];
}


// cb_load
// -------------------------------------

function cb_load(el, url) {
  if (!el.imageset_loaded) {
    el.setAttribute('data-image-set-loaded', true);
    el.dispatchEvent(new CustomEvent('image-set/loaded'));
    el.imageset_loaded = true;
  }

  el.style.backgroundImage = `url(${url})`;

  if (el.hasAttribute('data-image-set-active-class')) {
    const classes = el.getAttribute('data-image-set-active-class').split(' ');
    el.classList.add(...classes);
  }
}


// load_img
// -------------------------------------

function load_img(el, img_url) {
  if (el.imageset_promise) {
    el.imageset_promise.cancel();
  }

  el.imageset_promise = load_image(img_url);
  el.imageset_promise.then(() => cb_load(el, img_url));
}


// init_item
// -------------------------------------

function init_item(el) {
  el.sizes = get_image_set(el);
  if (!el.sizes || !el.sizes.length) {
    return;
  }

  const best_fit = best_size(el.sizes, el.offsetWidth * pixel_ratio());

  if (!el.cur_image_size || best_fit.w > el.cur_image_size.w) {
    el.cur_image_size = best_fit;
    load_img(el, best_fit.url);
  }
  el.initalized = true;
}


// cb_load_lazy_item & unbind_load_lazy_item
// -------------------------------------

function cb_load_lazy_item(ev) {
  const el = ev.currentTarget;

  init_item(el);
  unbind_load_lazy_item(el);

  const descendant_items = Array.from(el.querySelectorAll('[data-image-set-lazy]'));
  descendant_items.forEach((decendent) => {
    init_item(decendent);
    unbind_load_lazy_item(decendent);
  });
}

function unbind_load_lazy_item(el) {
  el.removeEventListener('image-set/load', cb_load_lazy_item);
}


// remove_items_no_longer_present_in_dom
// -------------------------------------

function remove_items_no_longer_present_in_dom() {
  state().items = state().items.filter(el => document.body.contains(el));
  state().lazy_items = state().lazy_items.filter((el) => {
    const keep = document.body.contains(el);
    if (!keep) {
      unbind_load_lazy_item(el);
    }
    return keep;
  });
}


// refresh - refresh all initialized items
// -------------------------------------

function refresh() {
  remove_items_no_longer_present_in_dom();

  // only refresh items that have already been initialized
  state().items
    .filter(el => el.initalized)
    .map(init_item);
}


// cb_resize
// -------------------------------------

function cb_resize() {
  clearTimeout(state().timeout);
  state().timeout = setTimeout(refresh, 250);
}


// Initialize
// -------------------------------------

function init() {
  const new_items = Array.from(document.body.querySelectorAll('[data-image-set],[data-image-set-wrapper]'))
    .filter(item => !state().items.includes(item));

  state().items       = new_items;
  state().lazy_items  = new_items.filter(el => el.hasAttribute('data-image-set-lazy') || el.hasAttribute('data-image-set-wrapper'));
  const regular_items = new_items.filter(el => !state().lazy_items.includes(el));

  regular_items.forEach(init_item);
  state().lazy_items.forEach(el => el.addEventListener('image-set/load', cb_load_lazy_item));


  if (!state().resize_bound) {
    window.addEventListener('resize', cb_resize);
    state().resize_bound = true;
  }
}
document.addEventListener('DOMContentLoaded', init);


// Interface
// -------------------------------------

export default {
  init,
  refresh,
};
